// Perps (§8): leveraged long/short on a live token, margined in DGN (gas coin). Synthetic: they
// track the token price but never trade on its curve. Liquidation = margin gone + heat.

import { TUNABLES } from "@degen/shared/config/tunables";
import { pushLog, type GameState } from "../state";
import { priceOf } from "../market/token";
import { addHeat } from "../player";
import { gas } from "@degen/shared/money";

const P = TUNABLES.perps;
const DGN_PRICE = () => TUNABLES.gas.priceWusd;

export interface PerpPosition {
  id: number;
  tokenId: string;
  ticker: string;
  side: "long" | "short";
  marginDgn: number;
  leverage: number;
  entry: number; // token price at open
  notionalWusd: number;
  openedTick: number;
  fundedDgn: number;
}

export interface PerpsState {
  open: PerpPosition[];
  nextId: number;
  realizedDgn: number;
  liquidations: number;
  best: number; // best closed PnL in DGN
}

export function createPerps(): PerpsState {
  return { open: [], nextId: 1, realizedDgn: 0, liquidations: 0, best: 0 };
}

type Result = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

/** PnL in DGN at the token's current price (funding already taken from margin). */
export function perpPnlDgn(state: GameState, pos: PerpPosition): number {
  const t = state.market.tokens[pos.tokenId];
  if (!t) return -pos.marginDgn;
  const move = priceOf(t) / pos.entry - 1;
  return (pos.notionalWusd * move * (pos.side === "long" ? 1 : -1)) / DGN_PRICE();
}

export function perpEquityDgn(state: GameState, pos: PerpPosition): number {
  return pos.marginDgn + perpPnlDgn(state, pos);
}

export function perpsValueWusd(state: GameState): number {
  return state.perps.open.reduce((a, pos) => a + Math.max(0, perpEquityDgn(state, pos)), 0) * DGN_PRICE();
}

export function openPerp(state: GameState, tokenId: string, side: "long" | "short", marginDgn: number, leverage: number): Result {
  const p = state.player;
  const t = state.market.tokens[tokenId];
  if (!t || t.phase === "dead" || t.rugged) return { ok: false, error: "No perps on dead coins." };
  if (state.perps.open.length >= P.maxOpen) return { ok: false, error: `Max ${P.maxOpen} open positions.` };
  if (!(leverage >= 1 && leverage <= P.maxLeverage)) return { ok: false, error: `Leverage 1-${P.maxLeverage}x.` };
  if (!(marginDgn >= P.minMarginDgn)) return { ok: false, error: `Min margin ${gas(P.minMarginDgn)}.` };
  const notionalWusd = marginDgn * DGN_PRICE() * leverage;
  const fee = (notionalWusd * P.feeRate) / DGN_PRICE();
  if (p.dgn + 1e-9 < marginDgn + fee) return { ok: false, error: `Need ${gas(marginDgn + fee)} (margin + fee).` };
  p.dgn -= marginDgn + fee;
  const pos: PerpPosition = { id: state.perps.nextId++, tokenId, ticker: t.ticker, side, marginDgn, leverage, entry: priceOf(t), notionalWusd, openedTick: state.tick, fundedDgn: 0 };
  state.perps.open.push(pos);
  return { ok: true, data: { id: pos.id } };
}

export function closePerp(state: GameState, id: number): Result {
  const pos = state.perps.open.find((x) => x.id === id);
  if (!pos) return { ok: false, error: "No such position." };
  const equity = perpEquityDgn(state, pos);
  const fee = (pos.notionalWusd * P.feeRate) / DGN_PRICE();
  const back = Math.max(0, equity - fee);
  state.player.dgn += back;
  const pnl = back - pos.marginDgn;
  state.perps.realizedDgn += pnl;
  if (pnl > state.perps.best) state.perps.best = pnl;
  state.perps.open = state.perps.open.filter((x) => x !== pos);
  return { ok: true, data: { pnl, back } };
}

/** Every 10 ticks: liquidations; every hour: funding. */
export function perpsTick(state: GameState) {
  if (!state.perps.open.length || state.tick % 10 !== 0) return;
  const hourly = state.tick % 60 === 0;
  for (const pos of [...state.perps.open]) {
    if (hourly) {
      const f = (pos.notionalWusd * P.fundingPerHour) / DGN_PRICE();
      pos.marginDgn -= f;
      pos.fundedDgn += f;
    }
    const t = state.market.tokens[pos.tokenId];
    const dead = !t || t.phase === "dead";
    if (dead || perpEquityDgn(state, pos) <= pos.marginDgn * P.maintenance) {
      state.perps.open = state.perps.open.filter((x) => x !== pos);
      state.perps.liquidations++;
      state.perps.realizedDgn -= pos.marginDgn;
      addHeat(state.player, P.liquidationHeat);
      pushLog(state, "liquidated", `💥 Liquidated: ${pos.leverage}x ${pos.side} on $${pos.ticker}. ${gas(pos.marginDgn)} margin gone. The timeline is laughing.`, { tokenId: pos.tokenId });
    }
  }
}
